<template>
  <div
    class="results-horizontal w-100 d-flex align-items-center justify-content-center"
  >
    <div
      v-if="emptyData && !editMode"
      class="d-flex align-items-center h-100 justify-content-around m-2 hera-muted"
    >
      {{
        $t('_web_match_results_data-not-found', 'Battle results data not found')
      }}
    </div>
    <div
      v-else
      class="w-100 d-flex flex-nowrap"
      :class="{
        'justify-content-between': rounds > 1,
        'justify-content-center': rounds < 2,
      }"
    >
      <div
        v-for="(round, index) in rounds"
        :key="index"
        class="round-box w-100 d-flex flex-column align-items-center"
      >
        <Scores
          v-if="playerOne.results || playerTwo.results"
          :value="
            isRoundConflicted(index)
              ? '-'
              : $lodash.isNumber(playerOne.results[index].player_1_scored)
              ? playerOne.results[index].player_1_scored
              : playerTwo.results[index].player_1_scored
          "
          :edit-mode="editMode"
          :has-errors="isRoundConflicted(index)"
          @update="setScores($event, 1, index)"
        />
        <div :class="[spacerClass]"></div>
        <Scores
          v-if="playerOne.results || playerTwo.results"
          :value="
            isRoundConflicted(index)
              ? '-'
              : $lodash.isNumber(playerOne.results[index].player_2_scored)
              ? playerOne.results[index].player_2_scored
              : playerTwo.results[index].player_2_scored
          "
          :edit-mode="editMode"
          :has-errors="isRoundConflicted(index)"
          @update="setScores($event, 2, index)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from 'vuex';
import Scores from '@/components/tournaments/SingleScore';
import { cloneDeep, isNumber } from '@/utils/lodashUtils';

export default {
  name: 'MatchResultsHorizontal',
  components: {
    Scores,
  },
  props: {
    match: {
      type: Object,
      required: true,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    isMatchConflicted: {
      type: Boolean,
      default: false,
    },
    isMatchFinished: {
      type: Boolean,
      default: false,
    },
    spacerClass: {
      type: String,
      default: 'mt-1',
    },
  },
  data() {
    return {
      errors: [],
      scoresBackup: {},
    };
  },
  computed: {
    ...mapGetters(['currentTournamentStage']),
    playerOne() {
      return {
        results: this.scoresBackup.player_1_match_results,
      };
    },
    playerTwo() {
      return {
        results: this.scoresBackup.player_2_match_results,
      };
    },
    rounds() {
      return this.scoresBackup.number_of_rounds;
    },
    emptyData() {
      return (
        this.playerOne.results &&
        this.playerTwo.results &&
        (!this.playerOne.results.length && !this.playerTwo.results.length)
      );
    },
  },
  watch: {
    editMode(newVal) {
      this.setBackup();
      if (newVal) {
        this.initTempMatchResults({
          battle_id: this.match.battle_id,
          player_1: this.playerOne.results,
          player_2: this.playerTwo.results,
        });
      }
    },
    match() {
      this.setBackup();
    },
  },
  created() {
    this.setBackup();
    //init
    if (this.editMode) {
      this.initTempMatchResults({
        battle_id: this.match.battle_id,
        player_1: this.playerOne.results,
        player_2: this.playerTwo.results,
      });
    }
  },
  methods: {
    ...mapMutations(['setTempMatchResults', 'initTempMatchResults']),
    ...mapActions(['errorNotify']),
    isRoundConflicted(index) {
      return (
        (!this.isMatchFinished &&
          (this.match.player_1_match_results[index].player_1_scored !== null &&
            this.match.player_2_match_results[index].player_1_scored !== null &&
            this.match.player_1_match_results[index].player_1_scored !==
              this.match.player_2_match_results[index].player_1_scored)) ||
        (this.match.player_1_match_results[index].player_2_scored !== null &&
          this.match.player_2_match_results[index].player_2_scored !== null &&
          this.match.player_1_match_results[index].player_2_scored !==
            this.match.player_2_match_results[index].player_2_scored)
      );
    },
    setScores(score, playerNumber, roundIndex) {
      if (this.playerOne.results && this.playerTwo.results) {
        const scores = {
          ...this.scoresBackup[`player_${playerNumber}_match_results`][
            roundIndex
          ],
          [`player_${playerNumber}_scored`]: score,
        };
        const hasTieGame =
          scores.player_1_scored === scores.player_2_scored &&
          scores.player_1_scored !== null &&
          scores.player_2_scored !== null;
        if (
          hasTieGame &&
          !this.currentTournamentStage.block_configuration.tie_game
        ) {
          this.errorNotify({
            message: this.$t(
              '_web_tournaments-tie-game-not-allowed',
              'Tie results are not allowed for this competition'
            ),
            inModal: true,
            yesText: this.$t('_web_form_error_confirm-yes', 'OK'),
          });
          this.$emit('allowSaveResult', false);
          return;
        }
        this.playerOne.results[scores.round] = this.playerTwo.results[
          scores.round
        ] = scores;

        const hasTieMatch = this.playerOne.results.reduce(
          (acc, results) => {
            const newP1 =
              acc.p1 + +(results.player_1_scored > results.player_2_scored);
            const newP2 =
              acc.p2 + +(results.player_2_scored > results.player_1_scored);
            return {
              p1: newP1,
              p2: newP2,
              tie:
                newP1 === newP2 &&
                results.player_1_scored !== null &&
                results.player_2_scored !== null,
            };
          },
          { p1: 0, p2: 0, tie: false }
        ).tie;
        if (
          hasTieMatch &&
          !this.currentTournamentStage.block_configuration.tie_match
        ) {
          this.errorNotify({
            message: this.$t(
              '_web_tournaments-tie-match-not-allowed',
              'Tie match are not allowed for this competition'
            ),
            inModal: true,
            yesText: this.$t('_web_form_error_confirm-yes', 'OK'),
          });
          this.$emit('allowSaveResult', false);
          return;
        }

        if (
          isNumber(scores.player_1_scored) &&
          isNumber(scores.player_2_scored)
        ) {
          //set results temp for match round
          this.setTempMatchResults({
            player_1: scores,
            player_2: scores,
            round: scores.round,
            battle_id: this.match.battle_id,
          });
          this.$emit('allowSaveResult', true);
        }
      }
    },
    setBackup() {
      this.scoresBackup = cloneDeep(this.match);
    },
  },
};
</script>

<style lang="scss" scoped>
.results-horizontal {
  position: relative;
  .loader {
    @include auto-rtl(right, 50%);
    transform: translateX(50%);
  }
  .round-box {
    //padding: 0 0.8rem;
    max-width: 40px;
    &:first-child {
      @include auto-rtl(padding-left, 0);
    }
    &:last-child {
      @include auto-rtl(padding-right, 0);
    }
    .scores {
      input {
        height: 24px;
      }
    }
  }
}
</style>
